import * as React from 'react';
import {useParams} from 'react-router-dom';
import {
  Box,
  Button,
  Cluster,
  FormItem,
  IValidatorField,
  Select,
  Stack,
  TextInput,
  Toast,
  Toggle,
  summon,
  useValidateForm,
} from '@pluto-tv/assemble';
import {useUserRegions} from 'helpers/useUserRegions';
import {IChannel, ISamsungChannelProperties} from 'models/channels';
import {orderBy} from 'lodash-es';
import {requiredField} from 'helpers/fieldValidators';
import {useFindByIdQuery} from 'features/channels/channelsApi';
import {ITerritory} from 'models/territory';
import {IActiveRegion} from 'models/activeRegions';

const emptyModel = {
  id: '',
  channel: '',
  serviceId: '',
  region: '',
  intileEnabled: false,
};

const searchChannels = async (
  samsungId: string,
  allRegions: IActiveRegion[] | undefined,
): Promise<{data: IChannel[]}> => {
  const regionsQuery = allRegions?.reduce((acc, r) => acc.concat(`&activeRegion=${r.code.toLowerCase()}`), '');
  return summon.get<void, {data: IChannel[]}>(`channels?samsungId=${samsungId}${regionsQuery}`);
};

const samsungServiceValidator: IValidatorField<ISamsungChannelProperties>[] = [
  {
    name: 'id',
    label: 'ID',
  },
  {
    name: 'intilesEnabled',
    label: 'Intiles Enabled',
  },
  ...[
    {name: 'serviceId', label: 'Service ID'},
    {name: 'region', label: 'Territory'},
  ].map(requiredField),
];

export interface ISamsungServiceFormProps {
  isNew?: boolean;
  value?: Partial<ISamsungChannelProperties>;
  onCancel(): void;
  onSave(savedObj: ISamsungChannelProperties): void;
  visible?: boolean;
}

const SamsungServiceForm = ({
  isNew = false,
  onCancel,
  value,
  onSave,
  visible,
}: ISamsungServiceFormProps): JSX.Element => {
  const {
    model,
    onChange,
    form,
    setModel,
    setFields,
    state: formState,
    onBlur,
  } = useValidateForm<ISamsungChannelProperties>(samsungServiceValidator, 'immediate');

  const [serviceIdError, setServiceIdError] = React.useState('');
  const [isAlreadyInserted, setIsAlreadyInserted] = React.useState(false);
  const [defaultTerritory, setDefaultTerritory] = React.useState<ITerritory>();
  const [territories, setTerritories] = React.useState<ITerritory[]>([]);

  const {id} = useParams<{id: string}>();
  const {territoriesByRegion, allRegions} = useUserRegions();
  const {data: channel} = useFindByIdQuery(id);

  const checkServiceId = React.useCallback(
    async serviceId => {
      /*
        Proceed with "serviceIdError" error if:
        1. the found channel is different on the current
          - Although business rules say that a "serviceId" can be only used by only one channel,
          it handles cases that may not be aligned with this rule.
        2. is the same channel and the user is not editing it.
        3. user just inserted as a new service id but didn't save it(it would be repeated)
      */
      try {
        if (serviceId) {
          const isNewAlreadyIncluded =
            (channel?.samsungChannelProperties &&
              channel?.samsungChannelProperties.filter(e => !e.id && e?.serviceId === serviceId)?.length) ||
            0;
          setIsAlreadyInserted(isNewAlreadyIncluded > 0);

          const serviceChannel = await searchChannels(serviceId, allRegions);
          const channelUsingServiceId = serviceChannel?.data.find(c => {
            const hasSamsungChannelProperties = !!c?.samsungChannelProperties?.length;
            const isSameChannel = c.id === id;
            return hasSamsungChannelProperties && (!isSameChannel || (isSameChannel && isNew));
          });
          setServiceIdError(
            channelUsingServiceId ? `This service ID is currently used in ${channelUsingServiceId.name}` : '',
          );
        }
      } catch (err: any) {
        if (err?.status === 404) {
          setServiceIdError('');
        } else {
          Toast.error('Error', 'Failed to check Service ID.');
          onCancel();
        }
      }
    },
    [onCancel, id, isNew, setServiceIdError, channel?.samsungChannelProperties, allRegions],
  );

  React.useEffect(() => {
    if (channel?.activeRegion) {
      const activeRegion = channel.activeRegion.toUpperCase();
      const arTerritories = territoriesByRegion[activeRegion] || [];
      const [arDefaultTerritory] = arTerritories.filter(el => el.id === channel.activeRegion.toUpperCase());

      setTerritories(arTerritories);
      setDefaultTerritory(arDefaultTerritory);
    }
  }, [territoriesByRegion, channel?.activeRegion]);

  React.useEffect(() => {
    if (visible && value) {
      setModel(value);
    } else {
      if (defaultTerritory) {
        setModel({
          ...emptyModel,
          region: defaultTerritory.id,
        });
      }
    }
  }, [defaultTerritory, setModel, visible, value]);

  return (
    <Box padding='small' background='charcoal' width='18rem'>
      <Stack space='small'>
        <FormItem
          {...form.serviceId}
          onBlur={() => {
            onBlur('serviceId', false);
            checkServiceId(model.serviceId || '');
          }}
          helpText={form.serviceId?.helpText || serviceIdError}
          state={form.serviceId?.state === 'error' || serviceIdError ? 'error' : ''}
        >
          <TextInput onChange={value => onChange('serviceId', value)} value={model.serviceId} id='serviceId' />
        </FormItem>
        <FormItem {...form.region} onBlur={() => onBlur('region', false)}>
          <Select
            placeholder='Select a Territory'
            value={{label: '', value: model.region?.toUpperCase()}}
            predicate='value'
            searchable={true}
            options={
              orderBy(
                territories.map(t => ({label: `${t.name} (${t.id})`, value: t.id.toUpperCase()})),
                'label',
              ) || []
            }
            onChange={val =>
              setFields({
                region: val.value,
              })
            }
          />
        </FormItem>
        <FormItem child='Toggle' label='Intiles Enabled'>
          <Toggle label='Yes' value={model.intilesEnabled} onChange={val => setFields({intilesEnabled: val})} />
        </FormItem>
        <Cluster justify='space-between'>
          <div></div>
          <Cluster space='small'>
            <Button ghost={true} onClick={() => onCancel()}>
              Cancel
            </Button>
            <Button
              type='primary'
              onClick={() => onSave(model as ISamsungChannelProperties)}
              state={
                !formState.isValid || !formState.isDirty || !!serviceIdError || isAlreadyInserted ? 'disabled' : ''
              }
            >
              {isNew ? '+ Add' : 'Update'}
            </Button>
          </Cluster>
        </Cluster>
      </Stack>
    </Box>
  );
};

export default SamsungServiceForm;
